<template>
  <div class="doc-section">
    <div class="section-header">
      <span class="material-icons-sharp section-icon">account_balance_wallet</span>
      <h2>Comptabilité</h2>
    </div>

    <p class="section-intro">
      Le module Comptabilité offre une vue d'ensemble sur les flux financiers :
      portefeuilles utilisateurs, demandes de sortie d'argent et vidage de comptes.
    </p>

    <!-- ===== PORTEFEUILLES ===== -->
    <h3 class="sub-title">Portefeuilles utilisateurs</h3>
    <p class="section-text">
      Le tableau principal affiche les soldes des comptes de chaque utilisateur (caissier, magasinier, etc.) répartis par méthode de paiement (espèces, chèque, virement).
    </p>

    <!-- ===== DEMANDE DE SORTIE D'ARGENT ===== -->
    <h3 class="sub-title">Faire une demande de sortie d'argent</h3>
    <p class="section-text">
      Tout utilisateur peut faire une demande pour justifier une sortie d'argent (achats, frais divers).
      La demande est soumise à l'administrateur pour validation.
    </p>

    <div class="table-wrapper">
      <table>
        <thead>
          <tr><th>Champ</th><th>Type</th><th>Obligatoire</th><th>Ce qu'il faut saisir</th></tr>
        </thead>
        <tbody>
          <tr><td><strong>Libellé</strong></td><td>Texte</td><td><span class="required-badge badge-yes">Oui</span></td><td>Description de la dépense (ex : « Achat fournitures bureau »).</td></tr>
          <tr><td><strong>Type de compte</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-yes">Oui</span></td><td>Le type de caisse concerné (Espèces, Chèque, Virement).</td></tr>
          <tr><td><strong>Montant</strong></td><td>Nombre</td><td><span class="required-badge badge-yes">Oui</span></td><td>Montant demandé.</td></tr>
          <tr><td><strong>Fichier preuve</strong></td><td>Fichier</td><td><span class="required-badge badge-no">Non</span></td><td>Justificatif (facture, reçu, photo).</td></tr>
          <tr><td><strong>Observation</strong></td><td>Texte</td><td><span class="required-badge badge-no">Non</span></td><td>Détails complémentaires.</td></tr>
        </tbody>
      </table>
    </div>

    <h4 class="sub-title">Procédure : Soumettre une demande</h4>
    <div class="steps">
      <div class="step-item">
        <div class="step-num">1</div>
        <p>Ouvrez le module <strong>Comptabilité</strong> depuis le menu latéral.</p>
      </div>
      <div class="step-item">
        <div class="step-num">2</div>
        <p>Cliquez sur <span class="btn-ref">Ajouter</span> dans la section « Demandes de sortie ».</p>
      </div>
      <div class="step-item">
        <div class="step-num">3</div>
        <p>Saisissez le <em>Libellé</em> de la dépense, sélectionnez le <em>Type de compte</em>, indiquez le <em>Montant</em>.</p>
      </div>
      <div class="step-item">
        <div class="step-num">4</div>
        <p>Joignez un <em>Fichier preuve</em> si disponible. Ajoutez une <em>Observation</em>.</p>
      </div>
      <div class="step-item">
        <div class="step-num">5</div>
        <p>Cliquez sur <span class="btn-ref">Soumettre</span>. La demande est envoyée à l'administrateur pour validation (voir module Sortie d'argent).</p>
      </div>
    </div>

    <!-- ===== VIDER UN COMPTE ===== -->
    <h3 class="sub-title">Vider le compte d'un utilisateur</h3>
    <p class="section-text">
      L'administrateur peut vider (retirer) de l'argent du compte d'un utilisateur.
      Cela concerne par exemple la collecte des recettes d'un caissier.
    </p>

    <div class="table-wrapper">
      <table>
        <thead>
          <tr><th>Champ</th><th>Type</th><th>Obligatoire</th><th>Ce qu'il faut saisir</th></tr>
        </thead>
        <tbody>
          <tr><td><strong>Nom</strong></td><td>Texte (désactivé)</td><td>—</td><td>Nom de l'utilisateur (affiché automatiquement).</td></tr>
          <tr><td><strong>Poste</strong></td><td>Texte (désactivé)</td><td>—</td><td>Rôle de l'utilisateur.</td></tr>
          <tr><td><strong>Soldes</strong></td><td>Nombre(s) (désactivé)</td><td>—</td><td>Soldes par méthode de paiement (affichés automatiquement).</td></tr>
          <tr><td><strong>Type de compte</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-yes">Oui</span></td><td>Le compte à vider (Espèces, Chèque, Virement).</td></tr>
          <tr><td><strong>Montant</strong></td><td>Nombre</td><td><span class="required-badge badge-yes">Oui</span></td><td>Montant à retirer.</td></tr>
        </tbody>
      </table>
    </div>

    <h4 class="sub-title">Procédure : Vider un compte</h4>
    <div class="steps">
      <div class="step-item">
        <div class="step-num">1</div>
        <p>Dans le tableau des portefeuilles, trouvez l'utilisateur et cliquez sur l'icône de gestion.</p>
      </div>
      <div class="step-item">
        <div class="step-num">2</div>
        <p>Les informations (Nom, Poste, Soldes) sont affichées automatiquement.</p>
      </div>
      <div class="step-item">
        <div class="step-num">3</div>
        <p>Sélectionnez le <em>Type de compte</em> à vider, saisissez le <em>Montant</em>.</p>
      </div>
      <div class="step-item">
        <div class="step-num">4</div>
        <p>Cliquez sur <span class="btn-ref">Vider le compte</span>. Le solde est mis à jour immédiatement.</p>
      </div>
    </div>

    <div class="info-card warning">
      <div class="card-title">
        <span class="material-icons-sharp" style="font-size:16px;color:#ff9800">warning</span>
        Attention
      </div>
      <p>
        Le vidage de compte est irréversible. Vérifiez le montant et le type de compte avant de confirmer.
      </p>
    </div>
  </div>
</template>
<script>
export default { name: "DocComptabilite" };
</script>
